import { INPUT_STATUSES } from '@services/feedView';

// Centralized human labels + formatters for the Feed Viewer, in the same spirit as
// src/features/scoreBackfill/format.js: one machine value never renders two ways across surfaces.

// The canonical "no value" mark. Exported because components render it too — a hand-typed glyph is
// indistinguishable from the em dashes this file's prose uses, so a change of empty-value treatment
// cannot be done by find-and-replace. It stays a STRING: several formatters below land in `title`
// and `aria-label` attributes, which cannot hold an element.
export const EM_DASH = '—';

const humanizeToken = (value) =>
  String(value)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export const MARKER_LABELS = {
  [INPUT_STATUSES.PRESENT]: 'Present',
  [INPUT_STATUSES.STALE]: 'Stale',
  [INPUT_STATUSES.MISSING]: 'Missing',
};

// What each marker means for the row it sits on — shown as the marker's tooltip so the table
// doesn't need a legend to be readable.
export const MARKER_DESCRIPTIONS = {
  [INPUT_STATUSES.PRESENT]: 'Fresh, or carried forward and still available.',
  [INPUT_STATUSES.STALE]: 'Carried forward, but not fresh under this input’s occasion rule.',
  [INPUT_STATUSES.MISSING]: 'No value at all — this row has no combined score.',
};

// Theme tokens for the marker dot. `missing` reads as the strongest signal because it's the state
// that nulls the row's score; `stale` is a caution, not a failure.
export const MARKER_TOKENS = {
  [INPUT_STATUSES.PRESENT]: 'positive',
  [INPUT_STATUSES.STALE]: 'warning',
  [INPUT_STATUSES.MISSING]: 'negative',
};

export const formatMarker = (status) => MARKER_LABELS[status] || (status ? humanizeToken(status) : 'Unknown');

// What a member marker means once its value is an average rather than one occasion's number.
//
// MARKER_DESCRIPTIONS cannot be reused as-is, and `missing` is the one that proves it: it reads "No
// value at all — this row has no combined score", and on an averaged row BOTH halves can be false.
// The company may well have a combined score, made from the occasions where the other members landed;
// and "no value at all" has to be scoped to the occasions folded together rather than to the feed.
//
// The derived word is only ever a summary, so every branch states the counts behind it. `stale` in
// particular now means "at least one of these was carried forward", which is a different claim from
// the per-occasion marker's, and a reader who is not told the number cannot tell which one is meant.
export const aggregatedMarkerDescription = (input) => {
  if (!input?.aggregated) return null;
  const occasions = input.occasions || 0;
  const withValue = input.occasionsWithValue || 0;
  const stale = input.staleOccasions || 0;
  const plural = (n) => `${n} occasion${n === 1 ? '' : 's'}`;

  if (!withValue) return `No value in any of the ${plural(occasions)} averaged here.`;
  const scope =
    withValue === occasions ? `Average of all ${plural(occasions)}` : `Average of ${withValue} of ${plural(occasions)}`;
  return stale ? `${scope}; ${stale} carried forward.` : `${scope}, all fresh.`;
};

// A null score is a real state (a missing input), not a zero. It renders as an em dash and must
// never be coerced.
export const formatScore = (score) =>
  typeof score === 'number' && isFinite(score)
    ? score.toLocaleString(undefined, { maximumFractionDigits: 4 })
    : EM_DASH;

// How many decimals the change column renders at — the same precision formatScore uses, so the two
// numbers beside each other never disagree about how exact they are.
const DELTA_DECIMALS = 4;

// The value the column can actually SHOW, rounded to that precision. Both the sign and the colour are
// decided on this rather than on the raw number: a rise of 0.00001 prints as '0' at four decimals,
// and a '+' or a green on that would claim a direction the digits on screen do not support. -0 is
// normalized away for the same reason.
//
// Exported because copy.js's scoreDeltaTitle captions the digits the cell is SHOWING rather than the
// raw number — branching on the raw value would caption a cell reading '0' as having moved.
export const displayedDelta = (delta) => {
  if (typeof delta !== 'number' || !isFinite(delta)) return null;
  const rounded = Number(delta.toFixed(DELTA_DECIMALS));
  return rounded === 0 ? 0 : rounded;
};

// The change against the previous occasion, signed so the direction reads without a comparison.
//
// A rise gets a literal '+'; a fall already carries its own '-'. Deliberately not Intl's
// `signDisplay: 'exceptZero'`, whose ICU support differs between the browsers this ships to and the
// jest runtime — a formatter tested one way and rendered another is worse than a hand-written plus.
//
// Zero renders as zero, unsigned. A company that scored the same twice has MOVED NOTHING, which is a
// measured fact and a different thing from having nothing to compare — the same distinction
// formatMemberships draws when it answers 'None' rather than an em dash. Only the second is a gap,
// and only the second is an em dash here.
export const formatScoreDelta = (delta) => {
  const shown = displayedDelta(delta);
  if (shown === null) return EM_DASH;
  const magnitude = shown.toLocaleString(undefined, { maximumFractionDigits: DELTA_DECIMALS });
  return shown > 0 ? `+${magnitude}` : magnitude;
};

// The theme token a change's DIRECTION wears — a token rather than a colour, for the same reason
// inputValueTint returns one: this module has no theme access, and resolving it here would put a hex
// value in a formatter.
//
// Null for an unmoved change as well as an absent one. The two render differently — '0' against an
// em dash — but neither has a direction, and colouring a flat move would make the column's palette
// say something the number doesn't.
export const scoreDeltaToken = (delta) => {
  const shown = displayedDelta(delta);
  if (shown === null || shown === 0) return null;
  return shown > 0 ? 'positive' : 'negative';
};

// The same answer in four states rather than two, for the surfaces that need to name a flat move
// apart from an absent one — the cell's data attribute, and anything reading it.
//
// Every surface of this column — the digits, the colour, this word, and the tooltip — resolves
// through displayedDelta, and that is the point. A change of 9.99e-6 prints as '0'; deciding the
// colour on the rounded value and the wording on the raw one would put a cell on screen reading '0'
// while its own tooltip called it a movement.
export const scoreDeltaDirection = (delta) => {
  const token = scoreDeltaToken(delta);
  if (token) return token;
  return displayedDelta(delta) === null ? 'none' : 'flat';
};

// The field is utc_knowable_at, so it renders in UTC — but as a date a person reads rather than as a
// machine timestamp. Deliberately not createTimeInstance().format(): this project's dayjs has no utc
// plugin loaded, so it would render the instant in the viewer's local zone while the column header
// still claimed UTC. Intl with an explicit timeZone does the same job honestly.
//
// hourCycle h23 rather than hour12: false, which renders midnight as 24:00 under some ICU versions.
const KNOWABLE_AT_FORMAT = {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
};

const utcDateTimeFormatter = (extra) => new Intl.DateTimeFormat(undefined, { ...KNOWABLE_AT_FORMAT, ...extra });

export const formatKnowableAt = (value) => {
  if (!value) return EM_DASH;
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value);
  return utcDateTimeFormatter().format(date);
};

// The same instant to the second, with its zone named, for the cell's tooltip: the column trades
// seconds for readability, and an occasion timestamp is provenance worth being able to read exactly.
export const formatKnowableAtExact = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value);
  return utcDateTimeFormatter({ second: '2-digit', timeZoneName: 'short' }).format(date);
};

// A date-only label for a UTC instant. Deliberately not createTimeInstance().format(): the window
// bounds are serialized as UTC midnight, and formatting those in the viewer's local zone shows the
// previous day anywhere west of Greenwich — the same trap formatKnowableAt avoids.
export const formatUtcDate = (value) => {
  if (!value) return EM_DASH;
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

// 'fundamental_earnings_screen_v1' -> 'Fundamental Earnings Screen (v1)'
export const formatFeedId = (id) => {
  if (!id) return EM_DASH;
  const parts = String(id)
    .split(/[_\s]+/)
    .filter(Boolean);
  return parts
    .map((part) =>
      /^v\d+$/i.test(part) ? `(${part.toLowerCase()})` : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    )
    .join(' ')
    .replace(/\s+\(/g, ' (');
};

// An input column's header: the member feed's own label when it has one, else its id, prettified.
export const inputColumnLabel = (column) => column?.label || formatFeedId(column?.feedId || column?.key);

export const formatCount = (n) => (typeof n === 'number' && isFinite(n) ? n.toLocaleString() : EM_DASH);

// "Sector", "Sector and Indices", "Sector, Market cap and Indices" — read aloud in a sentence, so
// the last separator is a word. Here rather than beside one of its callers: the chooser states this
// set in a notice and the page names it on the button that fixes it, and two spellings of one list
// is how the two stop agreeing.
export const listOf = (labels) => {
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

// The symbol here is a historical label, not an identity: the server resolves `ticker` point-in-time
// as-of the row's knowable_at, so a company that renamed shows its old ticker on its old rows and
// its new one on the recent ones. That is why this reads the row rather than a company lookup, and
// why a chip resolved through today's symbol table can legitimately read differently from the rows
// beside it. company_id is the identity; the label is what was true that day.
export const companyLabel = (row) => {
  if (!row) return EM_DASH;
  if (row.symbol && row.companyName) return `${row.symbol} — ${row.companyName}`;
  return row.symbol || row.companyName || (row.companyId != null ? `Company ${row.companyId}` : EM_DASH);
};

// One company wearing two tickers inside a single result is a rename that happened inside the
// window — not a duplicate and not a bug, but it does make the Company column look inconsistent, so
// it is worth saying once above the table instead of letting a reader discover it row by row.
//
// Keyed on company_id because that is the only thing that survives a rename. Symbols are ordered by
// the earliest knowable_at they were seen on, so the list reads oldest-to-newest — the order the
// change actually happened in, which row order alone does not guarantee.
export const tickerChanges = (rows) => {
  const byCompany = new Map();

  (rows || []).forEach((row, index) => {
    if (row?.companyId === null || row?.companyId === undefined || !row.symbol) return;
    const key = String(row.companyId);
    const entry = byCompany.get(key) || { companyId: row.companyId, companyName: null, symbols: new Map() };
    if (!entry.companyName && row.companyName) entry.companyName = row.companyName;

    // new Date(null) is the epoch, not an invalid date — a null stamp has to be rejected first or a
    // row with no knowable_at would claim to be the oldest sighting of its symbol.
    const time = row.knowableAt ? new Date(row.knowableAt).getTime() : NaN;
    const seenAt = isNaN(time) ? null : time;
    const seen = entry.symbols.get(row.symbol);
    if (!seen) {
      entry.symbols.set(row.symbol, { index, time: seenAt });
    } else if (seenAt !== null && (seen.time === null || seenAt < seen.time)) {
      seen.time = seenAt;
    }
    byCompany.set(key, entry);
  });

  return Array.from(byCompany.values())
    .filter((entry) => entry.symbols.size > 1)
    .map((entry) => ({
      companyId: entry.companyId,
      companyName: entry.companyName,
      symbols: Array.from(entry.symbols.entries())
        .sort(([, a], [, b]) => {
          if (a.time !== null && b.time !== null && a.time !== b.time) return a.time - b.time;
          if (a.time === null && b.time !== null) return 1;
          if (b.time === null && a.time !== null) return -1;
          return a.index - b.index;
        })
        .map(([symbol]) => symbol),
    }));
};

// Named in full up to a point: three renames is already a sentence, and a reader who needs the rest
// can find them in the Company column now that they know to look.
const MAX_NAMED_TICKER_CHANGES = 3;

// The chain within one company needs its own ceiling. Company count is bounded by how many firms
// renamed, which is small; symbols-per-company is bounded by nothing but the ticker table's own
// noise, and a wide window over a company with a churning symbol history will happily emit every
// one of them into a single sentence. The two ends are what the reader needs — what it used to be
// and what it is now — so the middle collapses to a count.
const MAX_NAMED_SYMBOLS = 4;

const describeChain = (symbols) => {
  if (symbols.length <= MAX_NAMED_SYMBOLS) return symbols.join(' → ');
  const skipped = symbols.length - 2;
  return `${symbols[0]} → … (${formatCount(skipped)} more) → ${symbols[symbols.length - 1]}`;
};

export const formatTickerChanges = (changes) => {
  const list = changes || [];
  if (list.length === 0) return null;

  const named = list
    .slice(0, MAX_NAMED_TICKER_CHANGES)
    .map((change) => `${describeChain(change.symbols)}${change.companyName ? ` (${change.companyName})` : ''}`);
  const unnamed = list.length - named.length;
  const subject = list.length === 1 ? '1 company' : `${formatCount(list.length)} companies`;

  return `${subject} changed ticker inside this window: ${named.join('; ')}${
    unnamed > 0 ? `, and ${formatCount(unnamed)} more` : ''
  }. Each row shows the ticker that was current when the row became knowable.`;
};

// The knowable-at span the returned rows cover — the window the result actually speaks for, which is
// narrower than the one that was asked for whenever the feed has no rows at the edges.
export const knowableRange = (rows) => {
  const stamps = (rows || [])
    .map((row) => row.knowableAt)
    .filter(Boolean)
    .map((value) => ({ value, time: new Date(value).getTime() }))
    .filter((entry) => !isNaN(entry.time));
  if (stamps.length === 0) return null;
  const sorted = [...stamps].sort((a, b) => a.time - b.time);
  return { from: sorted[0].value, to: sorted[sorted.length - 1].value };
};

// Tint for a member's value: green above zero, red below, and fainter the closer it sits to zero,
// so a column of markers reads as a heat strip at a glance. Returns a theme token plus an alpha
// rather than a color, keeping this module free of theme access.
const MIN_TINT_ALPHA = 0.06;
const MAX_TINT_ALPHA = 0.55;

export const inputValueTint = (value, scale = 1) => {
  if (typeof value !== 'number' || !isFinite(value) || value === 0) return null;
  const reference = typeof scale === 'number' && isFinite(scale) && scale > 0 ? scale : 1;
  const magnitude = Math.min(Math.abs(value) / reference, 1);
  const alpha = MIN_TINT_ALPHA + magnitude * (MAX_TINT_ALPHA - MIN_TINT_ALPHA);
  return { token: value > 0 ? 'positive' : 'negative', alpha: Number(alpha.toFixed(3)) };
};

// The magnitude that counts as full saturation. Member values are expected in -1..1, so that's the
// floor; a feed that returns wider values is normalized against its own maximum instead of having
// every cell max out.
export const inputValueScale = (rows) => {
  const magnitudes = (rows || [])
    .flatMap((row) => row.members || [])
    .map((input) => input.value)
    .filter((value) => typeof value === 'number' && isFinite(value))
    .map(Math.abs);
  return magnitudes.length ? Math.max(1, ...magnitudes) : 1;
};

// PIT enrichment. A null is a real answer — the master may not resolve as-of the row's date, and a
// macro row has no company — so each of these renders an em dash rather than a blank or a zero.
export const formatSector = (sector) => sector || EM_DASH;

// Compact by design: market caps run to eleven digits and would otherwise set the column's width.
export const formatMarketCap = (value) => {
  if (typeof value !== 'number' || !isFinite(value)) return EM_DASH;
  const abs = Math.abs(value);
  const [divisor, suffix] =
    abs >= 1e12 ? [1e12, 'T'] : abs >= 1e9 ? [1e9, 'B'] : abs >= 1e6 ? [1e6, 'M'] : abs >= 1e3 ? [1e3, 'K'] : [1, ''];
  const scaled = value / divisor;
  const decimals = suffix && Math.abs(scaled) < 100 ? 1 : 0;
  return `$${scaled.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}${suffix}`;
};

// The keys the server can actually put on a row, and what to call them.
//
// Two groups, and the difference matters to a reader: `sp500`, `nasdaq100` and `djia30` are REAL
// index constituency, tracked by add/remove date. Everything ending `_proxy_top_<a>_to_<b>` — and
// `top_1_to_5000`, which is the same thing under an inconsistent name — is a market-cap RANK BAND
// standing in for an index whose constituency we don't license. A band is not the index it proxies,
// so the label says "proxy" rather than quietly claiming to be Russell 1000.
//
// The first six keys below are the ones a row can carry that this map used to miss entirely; they
// fell through to humanizeToken and rendered as 'R2000 Proxy Top 1001 To 3000'. That was survivable
// inside a collapsed select and is not inside a always-visible chip row, which is why they are here.
//
// The rank range is deliberately NOT in the label — it would set the width of the whole chip row —
// and lives in INDEX_DESCRIPTIONS instead, which the chips hang off `title`.
const INDEX_LABELS = {
  // Market-cap proxies.
  sp500_proxy_top_1_to_500: 'S&P 500 proxy',
  r1000_proxy_top_1_to_1000: 'R1000 proxy',
  r2000_proxy_top_1001_to_3000: 'R2000 proxy',
  r3000_proxy_top_1_to_3000: 'R3000 proxy',
  adr_proxy_top_1_to_500: 'ADR 500',
  top_1_to_5000: 'Top 5000',
  // Real constituency.
  sp500: 'S&P 500',
  djia30: 'Dow 30',
  nasdaq100: 'Nasdaq 100',
  // Speculative: no server emits these today. Kept because they cost nothing and are right if real
  // coverage ever ships — but do not read this map as a census of what a row can hold.
  sp400: 'S&P 400',
  sp600: 'S&P 600',
  sp1500: 'S&P 1500',
  russell1000: 'Russell 1000',
  russell2000: 'Russell 2000',
  russell3000: 'Russell 3000',
};

// What the label drops. A proxy's whole meaning is its rank range and how often that range is
// re-cut, and two of these are otherwise indistinguishable on a chip — `sp500_proxy_top_1_to_500`
// and `adr_proxy_top_1_to_500` are the same 500 ranks over different universes.
//
// `top_1_to_5000` gets a sentence rather than a range because it is the one band that does not nest
// with the others: it is ranked over every company with a market-cap snapshot, with no
// classification predicate at all, so it admits funds and non-US-primary listings that no other band
// can contain. A reader who assumes it is the widest of a family is wrong about which family.
const INDEX_DESCRIPTIONS = {
  sp500_proxy_top_1_to_500: 'Top 500 by market cap, re-ranked quarterly',
  r1000_proxy_top_1_to_1000: 'Top 1,000 by market cap, re-ranked semi-annually',
  r2000_proxy_top_1001_to_3000: 'Ranks 1,001–3,000 by market cap, re-ranked semi-annually',
  r3000_proxy_top_1_to_3000: 'Top 3,000 by market cap, re-ranked semi-annually',
  adr_proxy_top_1_to_500: 'Top 500 foreign-domiciled US listings (ADRs), re-ranked quarterly',
  top_1_to_5000:
    'Top 5,000 of every company with a market cap, re-ranked semi-annually — the only band with no classification filter, so it includes funds and non-US-primary listings',
  sp500: 'S&P 500 constituents, by add and remove date',
  djia30: 'Dow Jones Industrial Average constituents, by add and remove date',
  nasdaq100: 'Nasdaq 100 constituents, by add and remove date',
};

export const formatIndexName = (name) => {
  if (!name) return EM_DASH;
  const key = String(name).toLowerCase();
  return INDEX_LABELS[key] || humanizeToken(name);
};

// Undefined rather than a fallback sentence: a `title` of the label again is noise a screen reader
// reads twice, and an unknown key has nothing true to say about itself.
export const describeIndex = (name) => (name ? INDEX_DESCRIPTIONS[String(name).toLowerCase()] : undefined);

// "None" rather than an em dash: belonging to no index is a fact about the company, not a gap in
// what the server could answer.
export const formatMemberships = (memberships) => {
  if (!memberships || memberships.length === 0) return 'None';
  return memberships.map(formatIndexName).join(', ');
};

// The payload columns a single-input feed carries (explanation, thinking, …). Names are declared
// per score spec, so they're titled generically rather than mapped.
export const formatPayloadKey = (key) => humanizeToken(key);

export const payloadEntries = (payload) =>
  Object.keys(payload || {})
    .sort()
    .map((key) => ({ key, label: formatPayloadKey(key), value: payload[key] }));

// The magnitude that counts as full saturation for the Score column, on the same footing as
// inputValueScale: scores are expected in -1..1, so that's the floor, and a feed that returns wider
// values is normalized against its own maximum rather than having every cell max out.
export const scoreScale = (rows) => {
  const magnitudes = (rows || [])
    .map((row) => row.score)
    .filter((score) => typeof score === 'number' && isFinite(score))
    .map(Math.abs);
  return magnitudes.length ? Math.max(1, ...magnitudes) : 1;
};
